import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isApiError } from '@/api/errors';
import { setPartnerOperatingMode } from '@/api/partners';
import type { OperatingMode } from '@/api/types';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const MODE_CONSEQUENCE: Record<OperatingMode, string> = {
  OPERATOR: "This partner's key will be able to read and act on the content of every org it owns.",
  DELEGATED:
    "Content permissions already on this partner's keys stop working immediately; remove them from the keys to keep things tidy.",
};

export function OperatingModeDialog({
  partnerId,
  partnerName,
  mode,
  onDone,
}: {
  partnerId: string;
  partnerName: string;
  /** The mode to switch to. */
  mode: OperatingMode;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();

  const change = useMutation({
    mutationFn: () => setPartnerOperatingMode(partnerId, mode),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['partners'] });
      onDone();
    },
  });

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Switch to {mode} mode?</DialogTitle>
        <DialogDescription>{partnerName}</DialogDescription>
      </DialogHeader>

      <p className="text-sm">{MODE_CONSEQUENCE[mode]}</p>

      {change.isError && (
        <Alert variant="destructive">
          <AlertDescription>
            {isApiError(change.error) ? change.error.message : 'Could not change the mode.'}
          </AlertDescription>
        </Alert>
      )}

      <DialogFooter>
        <Button
          variant={mode === 'OPERATOR' ? 'destructive' : 'default'}
          disabled={change.isPending}
          onClick={() => change.mutate()}
        >
          {change.isPending ? 'Switching…' : `Switch to ${mode}`}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
